import React from 'react';
import { Button } from '@mui/material';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

interface PlateErrorBoundaryProps {
    children: React.ReactNode;
    lastValueRef?: React.MutableRefObject<any>;
    onReset?: () => void;
}

interface PlateErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export class PlateErrorBoundary extends React.Component<PlateErrorBoundaryProps, PlateErrorBoundaryState> {
    constructor(props: PlateErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): PlateErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // Import the SDK directly — nothing assigns `window.Sentry` in this app
        // (@sentry/react v8 does not self-attach to window), so the old
        // `(window as any).Sentry` read silently downgraded every editor crash
        // to a console.error and Sentry never saw it.
        // `tags.module: 'plate-editor'` is load-bearing: alert rules filter on it.
        try {
            Sentry.captureException(error, {
                contexts: { react: { componentStack: info.componentStack } },
                tags: { module: 'plate-editor', boundary: 'PlateErrorBoundary' },
            });
        } catch {
            /* Sentry unavailable (not init'd outside prod) — fall through to console */
        }
        console.error('[PlateErrorBoundary]', error, info);
    }

    handleCopy = async () => {
        const value = this.props.lastValueRef?.current ?? [];
        try {
            await navigator.clipboard.writeText(JSON.stringify(value));
            toast.success('Copied to clipboard');
        } catch {
            toast.error('Failed to copy to clipboard');
        }
    };

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        this.props.onReset?.();
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <Card
                data-testid="plate-editor-error"
                sx={{ padding: 3, margin: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}
            >
                <Typography variant="h6">Editor crashed</Typography>
                <Typography variant="body2">
                    Something went wrong rendering the editor. Your last edit is still in memory — copy it to the clipboard, then reload the
                    editor.
                </Typography>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="outlined" onClick={this.handleCopy}>
                        Copy last content
                    </Button>
                    <Button variant="contained" onClick={this.handleReset}>
                        Reload editor
                    </Button>
                </div>
            </Card>
        );
    }
}
